import { Button, Select } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import type { DefaultOptionType } from 'rc-select/lib/Select';
import { useCallback, useMemo, useRef, useState } from 'react';

import './SpecificationDimensionsMultiselect.scss';

import type { InputStatus } from 'antd/lib/_util/statusUtils';
import {
  areMeasureUnitDimensionsValueAndOptionEqual,
  toCustomDimensionsDefaultType,
  toDimensionsDefaultType,
  toDimensionsOptionValue,
  toDimensionsValue,
} from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensions.functions';
import type {
  DimensionsOptionValue,
  DimensionsValue,
  MeasureUnitDimensionsValue,
} from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsField.types';
import {
  SpecificationDimensionsPopup,
  type SpecificationDimensionsPopupRef,
} from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsPopup';
import { cNs } from 'src/helpers';
import {
  type IProductSpecificationFieldReference,
  ProductSpecificationFieldType,
} from 'src/types/reference/IProductSpecificationReference';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function OptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

type SpecificationDimensionsMultiselectProps = {
  field: IProductSpecificationFieldReference;

  defaultValue?: DimensionsValue;

  onChange?: (selectedValue: DimensionsValue) => void;

  status?: InputStatus;
  disabled?: boolean;

  containerClassName?: string;
  className?: string;
};

export function SpecificationDimensionsMultiselect({
  field,
  defaultValue,
  onChange,

  status,
  disabled,

  containerClassName,
  className,
}: Readonly<SpecificationDimensionsMultiselectProps>) {
  const specificationMultiselectCascaderPopupRef = useRef<SpecificationDimensionsPopupRef>(null);

  const [selectedMeasureUnit, setSelectedMeasureUnit] = useState<SpecificationMeasureUnit>(
    (defaultValue != null
      ? Array.isArray(defaultValue)
        ? defaultValue[0]?.measureUnit
        : defaultValue.measureUnit
      : null) ??
      field.defaultMeasureUnit ??
      field.allowedMeasureUnits[0] ??
      SpecificationMeasureUnit.NONE
  );

  const [selectedValue, setSelectedValue] = useState<DimensionsValue>(defaultValue ?? (field.multiselect ? [] : null));
  const selectedOptionsValue = useMemo(() => toDimensionsOptionValue(selectedValue), [selectedValue]);

  const [customValues, setCustomValues] = useState<
    Partial<Record<SpecificationMeasureUnit, MeasureUnitDimensionsValue[]>>
  >(() => {
    if (defaultValue == null) {
      return {};
    }

    if (field.multiselect) {
      if (Array.isArray(defaultValue)) {
        // Check that default value is not in options already
        return defaultValue.reduce(
          (acc, curr) => {
            if (
              field.showOptions &&
              field.options?.[curr.measureUnit]?.some((optionItem) =>
                areMeasureUnitDimensionsValueAndOptionEqual(curr, optionItem)
              )
            ) {
              return acc;
            }

            acc[curr.measureUnit] = [curr, ...(acc[curr.measureUnit] ?? [])];

            return acc;
          },
          {} as Partial<Record<SpecificationMeasureUnit, MeasureUnitDimensionsValue[]>>
        );
      }

      // Check that default value is not in options already
      if (
        field.showOptions &&
        field.options?.[defaultValue.measureUnit]?.some((optionItem) =>
          areMeasureUnitDimensionsValueAndOptionEqual(defaultValue, optionItem)
        )
      ) {
        return {};
      }

      return {
        [defaultValue.measureUnit]: [defaultValue],
      };
    }

    if (Array.isArray(defaultValue)) {
      const itemsNotInOptions = defaultValue.filter(
        (defaultValueItem) =>
          // Check that default value is not in options already
          !field.showOptions ||
          !field.options?.[defaultValueItem.measureUnit]?.some((optionItem) =>
            areMeasureUnitDimensionsValueAndOptionEqual(defaultValueItem, optionItem)
          )
      );

      if (itemsNotInOptions.length === 0) {
        return {};
      }

      return {
        [itemsNotInOptions[0].measureUnit]: itemsNotInOptions,
      };
    }

    // Check that default value is not in options already
    if (
      field.showOptions &&
      field.options?.[defaultValue.measureUnit]?.some((optionItem) =>
        areMeasureUnitDimensionsValueAndOptionEqual(defaultValue, optionItem)
      )
    ) {
      return {};
    }

    return {
      [defaultValue.measureUnit]: [defaultValue],
    };
  });

  const [forceHideDropdown, setForceHideDropdown] = useState<boolean | undefined>(undefined);

  const allowedMeasureUnits: SpecificationMeasureUnit[] = useMemo(
    () =>
      field.allowedMeasureUnits.length > 0
        ? field.allowedMeasureUnits
        : (Object.keys(SpecificationMeasureUnit) as SpecificationMeasureUnit[]),
    [field.allowedMeasureUnits]
  );

  const measureUnitsOptions: DefaultOptionType[] = useMemo(
    () =>
      allowedMeasureUnits.map((measureUnit) => ({
        label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[measureUnit],
        value: measureUnit,
      })),
    [allowedMeasureUnits]
  );

  const fieldOptions: DefaultOptionType[] = useMemo(
    () =>
      field.multiselect
        ? // For multiselect show options for all allowed measure units (groupped by measure unit)
          allowedMeasureUnits.map((allowedMeasureUnit) => ({
            label: `${SPECIFICATION_MEASURE_UNIT_MAPPER[allowedMeasureUnit]}`,
            options: [
              // Collect custom value without measure units
              ...(customValues[allowedMeasureUnit]?.map(toCustomDimensionsDefaultType) ?? []),

              // Collect existed field values without measure units
              ...((field.showOptions ? field.options?.[allowedMeasureUnit]?.map(toDimensionsDefaultType) : null) ?? []),
            ],
          }))
        : [
            // Collect custom value without measure units
            ...(customValues[selectedMeasureUnit]?.map(toCustomDimensionsDefaultType) ?? []),

            // Collect existed field values without measure units
            ...((field.showOptions ? field.options?.[selectedMeasureUnit]?.map(toDimensionsDefaultType) : null) ?? []),
          ],
    [field.multiselect, allowedMeasureUnits, selectedMeasureUnit, customValues, field.showOptions, field.options]
  );

  const handleCustomValueAdd = useCallback(
    (newCustomValues: MeasureUnitDimensionsValue[]) => {
      if (newCustomValues.length === 0) {
        return;
      }

      setCustomValues((prev) =>
        newCustomValues.reduce(
          (acc, curr) => {
            acc[curr.measureUnit] = [curr, ...(acc[curr.measureUnit] ?? [])];

            return acc;
          },
          // Refresh object to cause hook updates
          { ...prev }
        )
      );

      // Automatically select newly added value
      setSelectedValue((prev) => {
        const newSelectedValues = field.multiselect
          ? Array.isArray(prev)
            ? [...prev, ...newCustomValues]
            : newCustomValues
          : (newCustomValues[0] ?? null);

        // ToDo: bad pattern to do it in setState function
        setTimeout(() => onChange?.(newSelectedValues));

        return newSelectedValues;
      });
    },
    [field.multiselect, onChange]
  );

  const handlePopupClose = useCallback(() => {
    setForceHideDropdown(undefined);
  }, []);

  const handleCustomClick = useCallback(() => {
    setForceHideDropdown(true);

    specificationMultiselectCascaderPopupRef.current?.open();
  }, []);

  const handleChange = useCallback(
    (_newValue: DimensionsOptionValue, selectedOptions?: DefaultOptionType | DefaultOptionType[] | null) => {
      const newSelectedValues = toDimensionsValue(selectedOptions);

      setSelectedValue(newSelectedValues);

      onChange?.(newSelectedValues);
    },
    [onChange]
  );

  const handleMeasureUnitsChange = useCallback(
    (newMeasureUnits: SpecificationMeasureUnit) => {
      setSelectedMeasureUnit(newMeasureUnits);

      setSelectedValue(field.multiselect ? [] : null);

      onChange?.(field.multiselect ? [] : null);
    },
    [field.multiselect, onChange]
  );

  return (
    <div className={cNs('specification-dimensions-multiselect__container', containerClassName)}>
      {field.name ? <div className="specification-dimensions-multiselect__label">{field.name}</div> : null}
      {field.description ? (
        <div className="specification-dimensions-multiselect__description">{field.description}</div>
      ) : null}
      <div className="specification-dimensions-multiselect__container__wrapper">
        <SpecificationDimensionsPopup
          ref={specificationMultiselectCascaderPopupRef}
          is3d={field.type === ProductSpecificationFieldType.DIM3D}
          multiselect={field.multiselect ?? false}
          defaultMeasureUnit={field.multiselect ? (field.defaultMeasureUnit ?? null) : selectedMeasureUnit}
          allowedMeasureUnits={
            field.multiselect
              ? allowedMeasureUnits
              : selectedMeasureUnit !== SpecificationMeasureUnit.NONE
                ? [selectedMeasureUnit]
                : []
          }
          onAddValue={handleCustomValueAdd}
          onClose={handlePopupClose}
        >
          <Select<DimensionsOptionValue>
            className={cNs('specification-dimensions-multiselect__select', className)}
            showSearch={true}
            open={forceHideDropdown === true ? false : undefined}
            value={selectedOptionsValue}
            options={fieldOptions}
            onChange={handleChange}
            mode={field.multiselect ? 'multiple' : undefined}
            status={status}
            disabled={disabled}
            dropdownRender={(menu) => (
              <div className="specification-dimensions-multiselect__dropdown__container">
                <div className="specification-dimensions-multiselect__header">
                  {field.type === ProductSpecificationFieldType.DIM3D ? 'Length x Width x Height' : 'Length x Width'}
                </div>

                {menu}

                {field.allowCustomValues && (
                  <div className="specification-dimensions-multiselect__add-button__container">
                    <Button type="primary" onClick={handleCustomClick}>
                      Add Custom Value
                    </Button>
                  </div>
                )}
              </div>
            )}
            optionRender={(option) =>
              option.data.isCustom ? (
                <div className="specification-dimensions-multiselect__custom-option">{option.label}</div>
              ) : (
                option.label
              )
            }
          />
        </SpecificationDimensionsPopup>

        {!field.multiselect && (
          <Select
            style={{ minWidth: 75 }}
            value={selectedMeasureUnit}
            options={measureUnitsOptions}
            onChange={handleMeasureUnitsChange}
            status={status}
            disabled={disabled}
            popupMatchSelectWidth={false}
            optionRender={({ value }) => <OptionRender value={value} />}
          />
        )}
      </div>
    </div>
  );
}
